import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useAuth } from '../../context/AuthContext';
import { SearchContext } from '../Layout';

const navItems = [
  { label: 'Home', path: '/dashboard', icon: '⌂' },
  { label: 'Send', path: '/send-test', icon: '✈' },
  { label: 'Results', path: '/results', icon: '▣' },
  { label: 'Clients', path: '/clients', icon: '◉' },
];

const moreItems = [
  { label: 'Follow Ups', path: '/flagged-follow-ups', icon: '🚩', description: 'Review flagged clients' },
  { label: 'Earnings', path: '/earnings', icon: '₵', description: 'Track payouts' },
  { label: 'Profile', path: '/profile', icon: '☻', description: 'Professional details' },
  { label: 'Security', path: '/change-password', icon: '♢', description: 'Password settings' },
];

const searchablePaths = ['/dashboard', '/results', '/clients', '/flagged-follow-ups'];
const getInitials = (user) => `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'VP';

const MobileLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const pageTitle = useMemo(() => {
    const allItems = [...navItems, ...moreItems];
    return allItems.find(item => item.path === location.pathname)?.label || 'Portal';
  }, [location.pathname]);

  const moreActive = useMemo(() => moreItems.some(item => item.path === location.pathname), [location.pathname]);
  const searchVisible = searchOpen;

  useEffect(() => {
    const nextQuery = new URLSearchParams(location.search).get('q') || '';
    setSearchQuery(nextQuery);
  }, [location.search]);


  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSearchOpen(false);
      return;
    }

    const searchTarget = searchablePaths.includes(location.pathname) ? location.pathname : '/results';
    navigate(`${searchTarget}?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setSheetOpen(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchOpen(false);
    if (new URLSearchParams(location.search).has('q')) {
      navigate(location.pathname, { replace: true });
    }
  };

  return (
    <Shell>
      <TopBar>
        <BrandCluster>
          <LogoMark><img src="/logo192.png" alt="Visioning" /></LogoMark>
          <TitleStack>
            <Eyebrow>Practitioner</Eyebrow>
            <Title>{pageTitle}</Title>
          </TitleStack>
        </BrandCluster>

        <TopActions>
          <IconButton
            type="button"
            onClick={() => setSearchOpen(prev => !prev)}
            aria-label={searchOpen ? 'Close search' : 'Open search'}
          >
            ⌕
          </IconButton>
          <AvatarButton type="button" onClick={() => setSheetOpen(true)} aria-label="Open profile menu">
            {getInitials(user)}
          </AvatarButton>
        </TopActions>
      </TopBar>

      <SearchPanel $open={searchVisible}>
        <SearchForm onSubmit={handleSearchSubmit}>
          <SearchIcon>⌕</SearchIcon>
          <SearchInput
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search clients, results, dates…"
            aria-label="Search practitioner portal"
          />
          {searchQuery && <ClearSearch type="button" onClick={clearSearch} aria-label="Clear search">×</ClearSearch>}
        </SearchForm>
      </SearchPanel>

      <Content>
        <SearchContext.Provider value={{ searchQuery, setSearchQuery }}>
          {children}
        </SearchContext.Provider>
      </Content>

      <BottomNav aria-label="Mobile practitioner navigation">
        {navItems.map(item => (
          <BottomNavItem key={item.path} to={item.path}>
            <span>{item.icon}</span>
            <small>{item.label}</small>
          </BottomNavItem>
        ))}
        <MoreButton type="button" onClick={() => setSheetOpen(true)} $active={moreActive || sheetOpen}>
          <span>•••</span>
          <small>More</small>
        </MoreButton>
      </BottomNav>

      <SheetBackdrop $open={sheetOpen} onClick={() => setSheetOpen(false)} />
      <MoreSheet $open={sheetOpen} role="dialog" aria-label="More portal actions" aria-modal={sheetOpen ? 'true' : undefined}>
        <Handle />
        <SheetProfile>
          <ProfileAvatar>{getInitials(user)}</ProfileAvatar>
          <div>
            <strong>{user?.firstName || 'Practitioner'} {user?.lastName || ''}</strong>
            <span>{user?.email || 'Mobile workspace'}</span>
          </div>
        </SheetProfile>
        <SheetGrid>
          {moreItems.map(item => (
            <SheetAction key={item.path} type="button" onClick={() => handleNavigate(item.path)}>
              <span>{item.icon}</span>
              <div>
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </div>
            </SheetAction>
          ))}
        </SheetGrid>
        <LogoutButton type="button" onClick={logout}>Sign out</LogoutButton>
      </MoreSheet>
    </Shell>
  );
};

const Shell = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.12), transparent 32%),
    linear-gradient(180deg, #eff7ff 0%, #f8fbff 42%, #ffffff 100%);
  color: #0f172a;
  padding: max(14px, env(safe-area-inset-top)) 14px calc(94px + env(safe-area-inset-bottom));
`;

const TopBar = styled.header`
  align-items: center;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(216, 231, 248, 0.92);
  border-radius: 24px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  position: sticky;
  top: max(10px, env(safe-area-inset-top));
  z-index: 45;
  backdrop-filter: blur(14px);
`;

const BrandCluster = styled.div`
  align-items: center;
  display: flex;
  gap: 11px;
  min-width: 0;
`;

const LogoMark = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #0369a1, #0ea5e9);
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(14, 165, 233, 0.24);
  display: flex;
  height: 44px;
  justify-content: center;
  overflow: hidden;
  width: 44px;

  img { height: 100%; object-fit: contain; width: 100%; }
`;

const TitleStack = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  color: #0f172a;
  font-size: 1.08rem;
  line-height: 1.15;
  margin: 2px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TopActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  align-items: center;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 15px;
  color: #0369a1;
  display: flex;
  font-size: 1.15rem;
  font-weight: 950;
  height: 42px;
  justify-content: center;
  touch-action: manipulation;
  width: 42px;
`;

const AvatarButton = styled.button`
  align-items: center;
  background: #0f2f57;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(15, 47, 87, 0.22);
  color: white;
  display: flex;
  font-weight: 900;
  height: 42px;
  justify-content: center;
  touch-action: manipulation;
  width: 42px;
`;

const SearchPanel = styled.div`
  display: grid;
  grid-template-rows: ${p => (p.$open ? '1fr' : '0fr')};
  margin: ${p => (p.$open ? '12px 0 14px' : '0')};
  overflow: hidden;
  transition: grid-template-rows 200ms ease, margin 200ms ease;
`;

const SearchForm = styled.form`
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(191, 219, 254, 0.92);
  border-radius: 20px;
  box-shadow: 0 14px 32px rgba(30, 64, 175, 0.08);
  display: flex;
  gap: 8px;
  min-height: 0;
  padding: 11px 14px;
`;

const SearchIcon = styled.span`
  color: #0284c7;
  font-size: 1.25rem;
  font-weight: 900;
`;

const SearchInput = styled.input`
  background: transparent;
  border: 0;
  color: #0f172a;
  flex: 1;
  font-size: 16px;
  outline: none;
  min-width: 0;

  &::placeholder { color: #94a3b8; }
`;

const ClearSearch = styled.button`
  align-items: center;
  background: #f1f5f9;
  border: 0;
  border-radius: 999px;
  color: #64748b;
  display: flex;
  font-size: 1rem;
  font-weight: 900;
  height: 28px;
  justify-content: center;
  width: 28px;
`;

const Content = styled.main`
  position: relative;
  z-index: 1;
`;

const BottomNav = styled.nav`
  align-items: center;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 26px;
  bottom: calc(10px + env(safe-area-inset-bottom));
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.16);
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(5, 1fr);
  left: 10px;
  padding: 8px;
  position: fixed;
  right: 10px;
  transition: opacity 180ms ease, transform 180ms ease;
  z-index: 60;
  backdrop-filter: blur(16px);

  body.follow-up-detail-open &,
  body.result-detail-open &,
  body.client-detail-open & {
    opacity: 0;
    pointer-events: none;
    transform: translateY(110%);
  }
`;

const navItemStyles = `
  align-items: center;
  border-radius: 18px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  font-size: 1.05rem;
  font-weight: 900;
  gap: 2px;
  justify-content: center;
  min-height: 54px;
  text-decoration: none;
  touch-action: manipulation;

  span { line-height: 1; }
  small { font-size: 0.65rem; font-weight: 950; }
`;

const BottomNavItem = styled(NavLink)`
  ${navItemStyles}

  &.active {
    background: linear-gradient(135deg, #e0f2fe, #dbeafe);
    color: #0369a1;
    transform: translateY(-1px);
  }
`;

const MoreButton = styled.button`
  ${navItemStyles}
  background: ${p => (p.$active ? 'linear-gradient(135deg, #e0f2fe, #dbeafe)' : 'transparent')};
  border: 0;
  color: ${p => (p.$active ? '#0369a1' : '#64748b')};
`;

const SheetBackdrop = styled.div`
  background: rgba(15, 23, 42, 0.38);
  inset: 0;
  opacity: ${p => (p.$open ? 1 : 0)};
  pointer-events: ${p => (p.$open ? 'auto' : 'none')};
  position: fixed;
  transition: opacity 180ms ease;
  z-index: 70;
`;

const MoreSheet = styled.section`
  background: white;
  border-radius: 30px 30px 0 0;
  bottom: 0;
  box-shadow: 0 -18px 48px rgba(15, 23, 42, 0.22);
  left: 0;
  max-height: calc(88vh - env(safe-area-inset-top));
  overflow-y: auto;
  padding: 10px 16px calc(20px + env(safe-area-inset-bottom));
  position: fixed;
  right: 0;
  transform: translateY(${p => (p.$open ? '0' : '110%')});
  transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 80;
  -webkit-overflow-scrolling: touch;
`;

const Handle = styled.div`
  background: #cbd5e1;
  border-radius: 99px;
  height: 5px;
  margin: 0 auto 16px;
  width: 46px;
`;

const SheetProfile = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #f8fafc, #eff6ff);
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  display: flex;
  gap: 12px;
  padding: 13px;

  strong { display: block; font-size: 0.95rem; }
  span { color: #64748b; display: block; font-size: 0.78rem; margin-top: 3px; overflow-wrap: anywhere; }
`;

const ProfileAvatar = styled.div`
  align-items: center;
  background: linear-gradient(135deg, #0369a1, #0ea5e9);
  border-radius: 18px;
  color: white;
  display: flex;
  flex-shrink: 0;
  font-weight: 900;
  height: 48px;
  justify-content: center;
  width: 48px;
`;

const SheetGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, 1fr);
  margin: 14px 0;
`;

const SheetAction = styled.button`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  color: #0f172a;
  display: flex;
  font-weight: 850;
  gap: 9px;
  min-height: 66px;
  padding: 0 12px;
  text-align: left;
  touch-action: manipulation;

  > span { font-size: 1.08rem; }
  strong { display: block; font-size: 0.86rem; }
  small { color: #64748b; display: block; font-size: 0.66rem; margin-top: 3px; }
`;

const LogoutButton = styled.button`
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 18px;
  color: #be123c;
  font-weight: 900;
  min-height: 50px;
  touch-action: manipulation;
  width: 100%;
`;

export default MobileLayout;
